import React from 'react';
import CreateAssessmentContainer from './create-assessment.container';
import { gql, useMutation, useQuery } from '@apollo/client';

// Queries
import { ASSESSMENT_FIELDS } from '../../../lib/queries/assessment.queries';
import { MEMBER_FIELDS } from '../../../lib/queries/member.queries';
import { createCacheModifier } from '../../../lib/cache/basic.cache';
import { getAuthUser, setAuthUserSettings } from '../../../lib/services/auth.service';
import { getActiveCompanyId, getCompany } from '../../../lib/services/company.service';

const CREATE_ASSESSMENT_MUTATION = gql`
  mutation CreateAssessment($data: CreateAssessmentInput!) {
    createAssessment(data: $data) {
      ...AssessmentFields
    }
  }

  ${ASSESSMENT_FIELDS}
`;

const GET_MEMBERS = gql`
  query GetMembers($companyId: ID!) {
    members(companyId: $companyId) {
      ...MemberFields
    }
  }
  ${MEMBER_FIELDS}
`;

const COPY_ASSESSMENT_ROLES = gql`
  mutation CopyAssessmentRoles($fromAssessmentId: ID!, $toAssessmentId: ID!, $memberIds: [ID!]!) {
    copyAssessmentRoles(
      fromAssessmentId: $fromAssessmentId
      toAssessmentId: $toAssessmentId
      memberIds: $memberIds
    ) {
      success
      copiedCount
    }
  }
`;

export const CreateAssessmentGraphQL = () => {
  const user = getAuthUser();

  const companyId = getActiveCompanyId();
  const { company } = getCompany(companyId);
  const currentAssessmentId = user?.settings.activeAssessmentId || null;

  const [mutation] = useMutation(CREATE_ASSESSMENT_MUTATION);
  const [copyRolesMutation] = useMutation(COPY_ASSESSMENT_ROLES);

  // Fetch members for the current company
  const { data: membersData } = useQuery(GET_MEMBERS, {
    variables: { companyId },
    skip: !companyId
  });

  const createAssessmentMutation = (data: any) => {
    return mutation({
      variables: { data },
      update: (cache, { data }) => {
        if (data) {
          const createdAssessment = data.createAssessment;

          cache.modify({
            fields: {
              assessments: createCacheModifier({
                cache,
                createdDoc: createdAssessment,
                fragment: ASSESSMENT_FIELDS,
                fragmentName: 'AssessmentFields',
                modelName: 'Assessment'
              })
            }
          });

          setAuthUserSettings({ activeAssessmentId: createdAssessment._id });
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        createAssessment: {
          __typename: 'Assessment',
          ...data
        }
      }
    });
  };

  const copyRolesForMembers = async (params: {
    fromAssessmentId: string | null;
    toAssessmentId: string;
    memberIds: string[];
  }) => {
    if (!params.fromAssessmentId || params.memberIds.length === 0) {
      console.log('Skipping role copy: no source assessment or members');
      return;
    }

    console.log('Copying roles:', {
      from: params.fromAssessmentId,
      to: params.toAssessmentId,
      memberCount: params.memberIds.length
    });

    try {
      const result = await copyRolesMutation({
        variables: {
          fromAssessmentId: params.fromAssessmentId,
          toAssessmentId: params.toAssessmentId,
          memberIds: params.memberIds
        }
      });

      if (result.data?.copyAssessmentRoles) {
        const { success, copiedCount } = result.data.copyAssessmentRoles;
        if (success) {
          console.log(`Successfully copied roles for ${copiedCount} members`);
        } else {
          console.error('Role copy failed on server');
        }
      }
    } catch (error) {
      console.error('Failed to copy assessment roles:', error);
      throw error; // Re-throw to handle in the calling function
    }
  };

  return (
    <CreateAssessmentContainer
      createAssessmentMutation={createAssessmentMutation}
      company={company}
      members={membersData?.members || []}
      currentAssessmentId={currentAssessmentId}
      copyRolesForMembers={copyRolesForMembers}
    />
  );
};
